import type { ApiCallConfig, AdditionalParams, ApiCallHandler, ApiClient } from './apiClient';
import type { AxiosError, AxiosRequestConfig, AxiosStatic } from 'axios';
import { asyncTimeout } from '../async/asyncTimeout';

export function simulatedAxiosError(config: AxiosRequestConfig = {}): AxiosError {
  const error: Partial<AxiosError> & Error = new Error('simulated http error: ' + config.method + ' ' + config.url);
  return Object.assign(error, {
    name: 'SimulatedHttpError',
    config,
    isAxiosError: true,
    code: '503',
    response: {
      status: 503
    },
    toJSON: () => ({
      config: {},
      code: '503'
    }),
  });
}

/**
 * Base class for I/O communication obects that can be told to simulate errors.
 */
class Quashable {
  protected likelihoodOfFailure?: number;
  setFailureRate(likelihoodOfFailure: number) {
    this.likelihoodOfFailure = likelihoodOfFailure;
  }

  async maybeThrowSimulatedAxiosError(requestDescription: string): Promise<void> {
    while (true) {
      const likelihood = this.likelihoodOfFailure;
      if (likelihood) {
        if (Math.random() < likelihood) {
          /*
            Sleep anywhere from 10 milliseconds to 10 seconds, but mostly near 10
            milliseconds, in order to simulate random server side problems.
          */
          await asyncTimeout(10000 / (1 + Math.random() * 1000));

          /*
            Re-roll if likelihood has changed. This prevents the impression of
            lingering errors when testing.
          */
          if (this.likelihoodOfFailure != likelihood) {
            continue;
          }

          console.log('*** Simulating failure for HTTP request: ' + requestDescription);
          throw simulatedAxiosError();
        }

        console.log('Allowing passthrough of HTTP request: ' + requestDescription);
      }
      return;
    }
  }
}

export class QuashableApiClient extends Quashable implements ApiClient {

  constructor(private readonly inner: ApiClient) {
    super();
    this.call = async (clientConfig: ApiCallConfig, path: string, method: string, additionalParams?: AdditionalParams, body?: any): Promise<any> => {
      await this.maybeThrowSimulatedAxiosError(method + ' ' + path);
      return this.inner.call(clientConfig, path, method, additionalParams, body);
    };
  }

  call: ApiCallHandler;

  config(): ApiCallConfig {
    return this.inner.config();
  }

  refreshAuth(): Promise<void> {
    return this.inner.refreshAuth();
  }
}

export class QuashableAxios extends Quashable {
  constructor(readonly axios: AxiosStatic) {
    super();
    axios.interceptors.request.use(async req => {
      await this.maybeThrowSimulatedAxiosError(req.method + ' ' + req.url);
      return req;
    });
  }
}
